import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, RefreshCw, ShieldAlert, Wrench } from "lucide-react";
import { fixApiSource, getApiHealth, runApiHealthNow } from "@/lib/api-health.functions";
import type { HealthRow } from "@/lib/api-health.types";

export const Route = createFileRoute("/admin/api-health")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "API health · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ApiHealthPage,
});

type Row = HealthRow;

const CADENCE_LABEL: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Yearly",
  continuous: "Continuous",
  release: "Per release",
};

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusBadge(status: string) {
  if (status === "ok") return <Badge className="bg-emerald-600 hover:bg-emerald-600">Healthy</Badge>;
  if (status === "degraded") return <Badge className="bg-amber-500 hover:bg-amber-500">Needs update</Badge>;
  if (status === "failing") return <Badge variant="destructive">Failing</Badge>;
  return <Badge variant="secondary">Unknown</Badge>;
}

function ApiHealthPage() {
  const read = useServerFn(getApiHealth);
  const run = useServerFn(runApiHealthNow);
  const fix = useServerFn(fixApiSource);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState<string | null>(null);
  const [fixResult, setFixResult] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows((await read({})) as Row[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load health data");
    } finally {
      setLoading(false);
    }
  }, [read]);

  useEffect(() => {
    void load();
  }, [load]);

  const checkNow = async (heal: boolean) => {
    setRunning(true);
    setError(null);
    try {
      setRows((await run({ data: { heal } })) as Row[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Health run failed");
    } finally {
      setRunning(false);
    }
  };

  const fixOne = async (sourceId: string) => {
    setFixing(sourceId);
    try {
      const row = (await fix({ data: { sourceId } })) as Row;
      setRows((prev) => prev.map((r) => (r.source_id === sourceId ? row : r)));
      setFixResult((prev) => ({
        ...prev,
        [sourceId]: `${new Date().toLocaleTimeString()} — ${
          row.status === "ok"
            ? "Repaired, source healthy."
            : row.status === "credential_missing"
              ? `Still blocked: add ${(row.missing_credentials ?? []).join(", ")} in project settings.`
              : `Still ${row.status}: ${row.message ?? "see notes"}`
        }`,
      }));
    } catch (e) {
      setFixResult((prev) => ({
        ...prev,
        [sourceId]: e instanceof Error ? e.message : "Retry failed",
      }));
    } finally {
      setFixing(null);
    }
  };

  const attention = rows.filter((r) => r.needs_attention || r.status === "failing");
  const contentRow = rows.find((r) => r.source_id === "content-monthly");
  const staleCount = Number((contentRow?.detail as Record<string, unknown> | undefined)?.staleCount ?? 0);
  const stalePages = ((contentRow?.detail as Record<string, unknown> | undefined)?.staleThisMonth ??
    []) as string[];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">API &amp; data health</h1>
          <p className="text-sm text-muted-foreground">
            Every external data source the site uses, when its data last updated, when the next update
            is due, and whether an automatic repair was needed.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void checkNow(false)} disabled={running}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Check now
          </Button>
          <Button onClick={() => void checkNow(true)} disabled={running}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />}
            Check &amp; auto-repair
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {attention.length > 0 && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>{attention.length} source(s) need a human</AlertTitle>
          <AlertDescription>
            {attention.map((r) => `${r.label}: ${r.message ?? "failing"}`).join(" — ")}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly page updates</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {contentRow ? (
            <div className="space-y-1">
              <div>
                Last refresh: <strong>{fmt(contentRow.last_data_update_at)}</strong> · Next due:{" "}
                <strong>{fmt(contentRow.next_data_update_at)}</strong>
              </div>
              <div>
                {staleCount === 0 ? (
                  <span className="text-emerald-700">All tracked pages refreshed within the last month.</span>
                ) : (
                  <span className="text-destructive">
                    {staleCount} page update(s) overdue{stalePages.length ? `: ${stalePages.join(", ")}` : ""}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">Run a check to populate.</span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data sources</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No checks recorded yet — run “Check &amp; auto-repair”.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3">Source</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Update cadence</th>
                    <th className="p-3">Data last updated</th>
                    <th className="p-3">Next update due</th>
                    <th className="p-3">Last checked</th>
                    <th className="p-3">Depends on / fallback</th>
                    <th className="p-3">Notes / auto-repair</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.source_id} className="border-b align-top last:border-0">
                      <td className="p-3">
                        <div className="font-semibold">{r.label}</div>
                        <div className="text-xs text-muted-foreground">{r.category}</div>
                      </td>
                      <td className="p-3">{statusBadge(r.status)}</td>
                      <td className="p-3">{CADENCE_LABEL[r.cadence] ?? r.cadence}</td>
                      <td className="p-3">{fmt(r.last_data_update_at)}</td>
                      <td className="p-3">{fmt(r.next_data_update_at)}</td>
                      <td className="p-3">
                        {fmt(r.last_check_at)}
                        {typeof r.latency_ms === "number" && (
                          <div className="text-xs text-muted-foreground">{r.latency_ms} ms</div>
                        )}
                      </td>
                      <td className="p-3 max-w-[260px]">
                        <div className="text-xs">{(r.depends_on ?? []).join(" · ") || "—"}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {r.credential_dependent
                            ? (r.missing_credentials ?? []).length
                              ? `Needs configuration: ${(r.missing_credentials ?? []).join(", ")}`
                              : "Configured"
                            : "No credentials required"}
                        </div>
                        {r.fallback_active && r.fallback_plan && (
                          <div className="mt-1 text-xs text-amber-700">Fallback in use: {r.fallback_plan}</div>
                        )}
                        {r.stale_threshold_days ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Stale after {r.stale_threshold_days} day(s)
                            {r.source_period ? ` · period ${r.source_period}` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <div>{r.message}</div>
                        {r.last_error && (
                          <div className="mt-1 text-xs text-destructive">Last error: {r.last_error}</div>
                        )}
                        {r.last_self_heal_result && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Auto-repair {fmt(r.last_self_heal_at)}: {r.last_self_heal_result}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={fixing === r.source_id || running}
                          onClick={() => void fixOne(r.source_id)}
                        >
                          {fixing === r.source_id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Wrench className="mr-2 h-4 w-4" />
                          )}
                          Retry / fix
                        </Button>
                        {fixResult[r.source_id] && (
                          <div className="mt-1 text-xs text-muted-foreground">{fixResult[r.source_id]}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
